import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store';
import { creditCostFor, fetchCreditsPricing } from '@/store/slices/aiCredits';

import { Icon, Skeleton, Text } from '../../../../components';
import { useFeatureGate } from '../../../../hooks/useFeatureGate';
import type { HomePeriod } from '../../../../mock-data/types';
import { ROUTE } from '../../../../routes';
import { HOME, HUB_AREA } from '../../../../theme/tokens';
import type { MarketingHubArea } from '../../../../theme/tokens.raw';
import { marketingAreaDescription, marketingAreaTitle } from '../../../marketing-assets/area-copy';
import { visibleMarketingAreas, type MarketingAreaId } from '../../../marketing-assets/areas';
import { MarketingGlyph } from '../../../marketing-assets/components/Glyphs';
import { HubGlyph } from '../../../website/components/HubGlyph';
import { openRowUnlessControl } from '../../../creatives/components/AdLibraryShell/row-open';
import { assetKindOpens, useAssetOverlay } from '../../../creatives/components/AssetComposerOverlay';
import { openInApp } from '../../../creatives/components/LandingPageComposer/link-for';
import { revealOnLoad } from '../../../creatives/reveal-media';
import { linkKindLabel } from '../../../creatives/link-attribution';
import type { TrackingLinkKind } from '../../../creatives/tracking-links-api';
import type { TopAssetRow } from '../../top-marketing-assets';
import { TOP_ASSETS_LIMIT } from '../../top-marketing-assets';
import { useHasMarketingAssets } from '../../useHasMarketingAssets';
import { useTopMarketingAssets, type TopAssetsStatus } from '../../useTopMarketingAssets';
import { MetricFrame } from '../MetricFrame';
import { StatValue } from '../StatValue';
import {
  List,
  LabelRow,
  ColLabel,
  Row,
  Thumb,
  ThumbImg,
  NameCell,
  NameLine,
  NameLink,
  PlainName,
  KindLine,
  ViaCell,
  ViaPrefix,
  ViaLink,
  Muted,
  More,
  Figures,
  Fig,
  FigValue,
  FigLabel,
  ChevCell,
  Chev,
  Footer,
  FooterLink,
  EmptyCopy,
  EmptyCta,
  EmptyStage,
  EmptyTitle,
  EmptyText,
  GhostBar,
  GhostFigures,
  GhostLines,
  GhostList,
  GhostRow,
  GhostThumb,
  Quiet,
  Shortcut,
  ShortcutArrow,
  ShortcutLink,
  Shortcuts,
  ShortcutText,
  ShortcutTile,
  ShortcutWords,
  THUMB_PX,
} from './TopMarketingAssets.styles';

/** The kind's glyph, the one its Marketing hub tile uses. */
const KindGlyph = ({ kind }: { kind: TrackingLinkKind }): React.ReactElement => {
  switch (kind) {
    case 'video':
      return <MarketingGlyph name="video" size={22} />;
    case 'playable':
      return <MarketingGlyph name="mini-game" size={22} />;
    case 'banner':
      return <MarketingGlyph name="html5" size={22} />;
    case 'kit':
      return <Icon name="image" size={22} />;
    default:
      return <Icon name="globe" size={22} />;
  }
};

/** The empty state's ghost rows (what can rank here), each in its Marketing hub area's colours and glyph. */
const GHOST_ROWS: readonly { area: MarketingHubArea; glyph: (ink: string) => React.ReactNode }[] = [
  { area: 'videos', glyph: (ink) => <MarketingGlyph name="video" size={22} color={ink} /> },
  { area: 'banner-kits', glyph: (ink) => <Icon name="image" size={22} color={ink} /> },
  { area: 'landing-pages', glyph: (ink) => <Icon name="globe" size={22} color={ink} /> },
];

/** The areas a first ad is offered in, as shortcuts in the no-assets state. */
const SHORTCUT_AREAS: readonly MarketingAreaId[] = ['banner-kits', 'videos'];

/** Shortcuts into the Image Banners and Video Ads libraries, in their Marketing hub cards' colours, glyphs and copy.
 *  Each shows under exactly the flag (and, for Video Ads, the per-beat price) that shows its library and hub card. */
const StartShortcuts = (): React.ReactElement | null => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isEnabled } = useFeatureGate();
  const videoPriced = useAppSelector((state) => creditCostFor(state.aiCredits?.pricing, 'video_beat')) !== undefined;
  // Syncs with the network: the credits price table, which Video Ads' library needs (as on the Marketing hub).
  React.useEffect(() => {
    void dispatch(fetchCreditsPricing());
  }, [dispatch]);
  const areas = visibleMarketingAreas(isEnabled, videoPriced).filter((area) => SHORTCUT_AREAS.includes(area.id));
  if (areas.length === 0) return null;
  return (
    <Shortcuts>
      {areas.map(({ id, glyph, path }) => (
        <Shortcut key={id} element="li" glow={HUB_AREA[id].glow} interactive data-testid={`home-shortcut-${id}`}>
          <ShortcutTile>
            <HubGlyph area={id} span={24}>
              {(ink) =>
                glyph.kind === 'icon' ? (
                  <Icon name={glyph.name} size={22} color={ink} />
                ) : (
                  <MarketingGlyph name={glyph.name} size={22} color={ink} />
                )
              }
            </HubGlyph>
          </ShortcutTile>
          <ShortcutWords>
            <ShortcutLink to={path}>
              <Text text={marketingAreaTitle(t, id)} raw tag="span" />
            </ShortcutLink>
            <ShortcutText text={marketingAreaDescription(t, id)} raw tag="span" />
          </ShortcutWords>
          <ShortcutArrow aria-hidden="true">
            <Icon name="arrow-right-circle" size={20} color={HOME.t50} />
          </ShortcutArrow>
        </Shortcut>
      ))}
    </Shortcuts>
  );
};

/** No ranked assets yet: a faint preview of the rows that will rank here, beside what to do (literal copy per case). */
const EmptyPreview = ({ noLinks }: { noLinks: boolean }): React.ReactElement => {
  // Without links, whether the brand has made any asset yet decides the action: make one first, or get a link for one.
  const hasAssets = useHasMarketingAssets(noLinks);
  const noAssets = noLinks && hasAssets === 'no';
  return (
    <EmptyStage>
      <EmptyCopy>
        {noAssets ? (
          <>
            <EmptyTitle text="Start with your first ad" tag="paragraph" />
            <EmptyText
              text="Make an ad or a landing page, then share its tracking link. The ones that bring you players rank here."
              tag="paragraph"
            />
          </>
        ) : noLinks ? (
          <>
            <EmptyTitle text="Your best marketing assets will rank here" tag="paragraph" />
            <EmptyText
              text="Open one of your assets and get its tracking link. The ads and landing pages that bring you players show up here, with what they brought in."
              tag="paragraph"
            />
          </>
        ) : (
          <>
            <EmptyTitle text="No players through your links yet" tag="paragraph" />
            <EmptyText
              text="Your tracking links are out there. As soon as someone signs up through one, its asset ranks here."
              tag="paragraph"
            />
          </>
        )}
        <EmptyCta to={ROUTE.marketingAssets()}>
          {noAssets ? (
            <Text text="Create your first marketing asset" />
          ) : noLinks ? (
            <Text text="Get a tracking link" />
          ) : (
            <Text text="Go to marketing assets" />
          )}
          <Icon name="arrow-right-circle" size={18} />
        </EmptyCta>
        {noAssets ? <StartShortcuts /> : null}
      </EmptyCopy>
      <GhostList aria-hidden="true">
        {GHOST_ROWS.map(({ area, glyph }, i) => (
          <GhostRow key={area} element="li" glow={HUB_AREA[area].glow} $depth={i}>
            <GhostThumb>
              {/* The Marketing hub card's ink for this kind of asset (its glyph sweep). */}
              <HubGlyph area={area} span={24}>
                {glyph}
              </HubGlyph>
            </GhostThumb>
            <GhostLines>
              <GhostBar $w={['58%', '46%', '52%'][i] ?? '50%'} />
              <GhostBar $w="24%" />
            </GhostLines>
            <GhostFigures>
              <GhostBar $w="20px" />
              <GhostBar $w="16px" />
              <GhostBar $w="28px" />
            </GhostFigures>
          </GhostRow>
        ))}
      </GhostList>
    </EmptyStage>
  );
};

/** The asset's thumbnail over a neutral tile with its kind glyph (the glyph stays when there is no picture or it
 *  fails to load). */
const AssetThumb = ({ src, kind }: { src: string; kind: TrackingLinkKind }): React.ReactElement => {
  const [failed, setFailed] = React.useState(false);
  return (
    <Thumb aria-hidden="true">
      <KindGlyph kind={kind} />
      {src && !failed ? (
        <ThumbImg key={src} src={src} alt="" loading="lazy" {...revealOnLoad} onError={() => setFailed(true)} />
      ) : null}
    </Thumb>
  );
};

const LabelHeader = (): React.ReactElement => (
  <LabelRow aria-hidden="true">
    <ColLabel $area="name" $fromThumb text="Asset" />
    <ColLabel $area="via" text="Via landing page" />
    <ColLabel $area="reg" $end text="Registrations" />
    <ColLabel $area="ftd" $end text="FTDs" />
    <ColLabel $area="dcount" $end text="Deposits" />
    <ColLabel $area="dep" $end text="Deposit volume" />
  </LabelRow>
);

/** A row while the ranking loads: the same card and row, so nothing shifts on reveal. */
const SkeletonRow = ({ index }: { index: number }): React.ReactElement => (
  <Row as="li" $index={index} $enter={false} $locked aria-hidden="true">
    <Thumb>
      <Skeleton width={THUMB_PX} height={THUMB_PX} radius={10} block />
    </Thumb>
    <NameCell>
      <NameLine>
        <Skeleton width={`${62 - index * 6}%`} height={14} radius={5} />
      </NameLine>
      <KindLine>
        <Skeleton width={72} height={11} radius={5} />
      </KindLine>
    </NameCell>
    <ViaCell>
      <Skeleton width="55%" height={12} radius={5} />
    </ViaCell>
    <Figures>
      <Fig $area="reg">
        <Skeleton width={36} height={14} radius={5} />
      </Fig>
      <Fig $area="ftd">
        <Skeleton width={28} height={14} radius={5} />
      </Fig>
      <Fig $area="dcount">
        <Skeleton width={28} height={14} radius={5} />
      </Fig>
      <Fig $area="dep">
        <Skeleton width={52} height={14} radius={5} />
      </Fig>
    </Figures>
  </Row>
);

type AssetRowProps = {
  row: TopAssetRow;
  index: number;
  /** Open an asset in place (else its links navigate to its page). */
  onOpenAsset?: (kind: TrackingLinkKind, creativeId: string) => void;
};

/** One ranked asset: a channel card holding one row (thumbnail, name and kind, the landing page it goes through, its
 *  three figures, the open button). The whole card opens the asset, as a channel card does. */
const AssetRow = ({ row, index, onOpenAsset }: AssetRowProps): React.ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isEnabled } = useFeatureGate();
  const { link, landing, otherDestinations, isLandingPage } = row;
  const assetLinked = assetKindOpens(link.kind, isEnabled);
  const landingLinked = assetKindOpens('landing_page', isEnabled);
  const delay = index * 60;

  const openAsset = (): void => {
    if (onOpenAsset) onOpenAsset(link.kind, link.creative_id);
    else navigate(row.path);
  };
  // A plain click opens the asset in place; a modified or middle click keeps the link's own page (a new tab).
  const onAssetLink = onOpenAsset
    ? (event: React.MouseEvent<HTMLAnchorElement>): void => openInApp(event, row.path, openAsset)
    : undefined;
  const onLandingLink =
    onOpenAsset && landing
      ? (event: React.MouseEvent<HTMLAnchorElement>): void =>
          openInApp(event, landing.path, () => onOpenAsset('landing_page', landing.id))
      : undefined;

  return (
    <Row
      as="li"
      $index={index}
      $enter
      $locked={!assetLinked}
      onClick={
        assetLinked ? (event: React.MouseEvent<HTMLElement>) => openRowUnlessControl(event, openAsset) : undefined
      }
    >
      <AssetThumb src={row.imageUrl} kind={link.kind} />
      <NameCell>
        <NameLine>
          {assetLinked ? (
            <NameLink to={row.path} title={row.name} onClick={onAssetLink}>
              <Text raw text={row.name} />
            </NameLink>
          ) : (
            <PlainName raw text={row.name} title={row.name} />
          )}
        </NameLine>
        <KindLine>
          <Text raw text={linkKindLabel(link, t)} />
          {row.archived ? (
            <>
              <span aria-hidden="true">·</span>
              <Text text="Archived" />
            </>
          ) : null}
        </KindLine>
      </NameCell>
      <ViaCell>
        {isLandingPage ? null : <ViaPrefix text="via" />}
        {isLandingPage ? null : landing ? (
          landingLinked ? (
            <ViaLink to={landing.path} title={landing.name} onClick={onLandingLink}>
              <Text raw text={landing.name} />
            </ViaLink>
          ) : (
            <PlainName raw text={landing.name} title={landing.name} />
          )
        ) : (
          <Muted text="Casino" />
        )}
        {otherDestinations.length > 0 ? (
          <More text="+{{n}}" placeholders={{ n: otherDestinations.length }} title={otherDestinations.join(', ')} />
        ) : null}
      </ViaCell>
      <Figures>
        <Fig $area="reg">
          <FigValue>
            <StatValue value={row.registrations} delay={delay} />
          </FigValue>
          <FigLabel text="Registrations" />
        </Fig>
        <Fig $area="ftd">
          <FigValue>
            <StatValue value={row.ftds} delay={delay + 40} />
          </FigValue>
          <FigLabel text="FTDs" />
        </Fig>
        <Fig $area="dcount">
          <FigValue>
            <StatValue value={row.deposits} delay={delay + 80} />
          </FigValue>
          <FigLabel text="Deposits" />
        </Fig>
        <Fig $area="dep">
          <FigValue>
            <StatValue value={row.depositAmount} prefix="$" delay={delay + 120} />
          </FigValue>
          <FigLabel text="Deposit volume" />
        </Fig>
      </Figures>
      {assetLinked ? (
        <ChevCell>
          <Chev
            as={Link}
            to={row.path}
            className="chev"
            aria-label={t('Open {{name}}', { name: row.name })}
            onClick={onAssetLink}
          >
            <Icon name="arrow-right-circle" size={24} color={HOME.t50} />
          </Chev>
        </ChevCell>
      ) : null}
    </Row>
  );
};

export type TopMarketingAssetsProps = {
  status: TopAssetsStatus;
  rows: TopAssetRow[];
  /** Open an asset in place (its composer over the page); without it, an asset's links navigate to its page. */
  onOpenAsset?: (kind: TrackingLinkKind, creativeId: string) => void;
};

/**
 * "Top marketing assets": the brand's assets ranked by the first-time depositors their tracking links brought in the
 * Home range (then registrations), one compact row each (in an Acquisition Channels card) with the landing page its
 * link goes through and its figures. Presentational; {@link TopMarketingAssetsSection} feeds it.
 */
export const TopMarketingAssets = ({ status, rows, onOpenAsset }: TopMarketingAssetsProps): React.ReactElement => {
  const { t } = useTranslation();
  const tabular = status === 'loading' || status === 'ok';
  const empty = status === 'no-links' || status === 'no-players';
  return (
    // Empty: no head, so the focus stays on the empty state's own heading and action.
    <MetricFrame
      title={empty ? undefined : t('Top marketing assets')}
      sub={empty ? undefined : t('Ranked by first-time depositors')}
    >
      {tabular ? (
        <>
          <LabelHeader />
          <List aria-busy={status === 'loading' || undefined}>
            {status === 'loading'
              ? Array.from({ length: TOP_ASSETS_LIMIT }, (_, i) => <SkeletonRow key={i} index={i} />)
              : rows.map((row, i) => <AssetRow key={row.key} row={row} index={i} onOpenAsset={onOpenAsset} />)}
          </List>
        </>
      ) : null}
      {status === 'error' ? <Quiet text="Marketing asset stats are unavailable right now." tag="paragraph" /> : null}
      {status === 'no-links' || status === 'no-players' ? <EmptyPreview noLinks={status === 'no-links'} /> : null}
      {tabular || status === 'error' ? (
        <Footer>
          <FooterLink to={ROUTE.marketingAssets()}>
            <Text text="All marketing assets" />
            <Icon name="arrow-right-circle" size={18} />
          </FooterLink>
        </Footer>
      ) : null}
    </MetricFrame>
  );
};

export type TopMarketingAssetsSectionProps = {
  /** Active brand id (`displayBrand?.id`). */
  casinoInstanceId: string | undefined;
  /** The Home range. */
  interval: HomePeriod;
};

/** The section wired to its data (`useTopMarketingAssets`), opening assets in place (`useAssetOverlay`, whose
 *  `?asset=` marker it renders); closing one re-reads the brand's links (a rename or a moved link shows at once).
 *  Mounted only while Marketing Assets is available. */
export const TopMarketingAssetsSection = ({
  casinoInstanceId,
  interval,
}: TopMarketingAssetsSectionProps): React.ReactElement => {
  const { status, rows, reloadLinks } = useTopMarketingAssets(casinoInstanceId, interval);
  const overlay = useAssetOverlay({ onClosed: reloadLinks });
  return (
    <>
      <TopMarketingAssets status={status} rows={rows} onOpenAsset={overlay.open} />
      {overlay.node}
    </>
  );
};

export default TopMarketingAssets;
